/**
 * Share-link title templates for pages and media (.img) URLs.
 *
 * Media: site-wide `properties.media_share_title` (default `{parent} – {site}`).
 * Pages: per-page `og.title` template (default `{title}` = page name).
 * Variables `{parent}`, `{site}`, `{title}` are filled at resolve time.
 */

export const SHARE_TITLE_PROPERTY = 'media_share_title';

/** Site-wide description template for media (.img / .vid) share cards. */
export const SHARE_DESCRIPTION_PROPERTY = 'media_share_description';

/** Default for media (.img) share cards. */
export const SHARE_TITLE_DEFAULT = '{parent} – {site}';

/** Default media share description — the image or video title. */
export const SHARE_DESCRIPTION_DEFAULT = '{title}';

/** Default for page share cards — resolves to the page name. */
export const PAGE_SHARE_TITLE_DEFAULT = '{title}';

/** Insertable tokens shown in the share-card chip editor. */
export const SHARE_TITLE_VARS = [
    { key: 'parent', label: 'Parent page', sample: 'Work' },
    { key: 'site', label: 'Site title', sample: 'Tofjan' },
    { key: 'title', label: 'Title', sample: 'Work' },
];

/**
 * @param {string|null|undefined} template
 * @param {string} [fallback]
 * @returns {string}
 */
export function normalizeShareTitleTemplate(template, fallback = SHARE_TITLE_DEFAULT) {
    const trimmed = String(template ?? '').trim();
    return trimmed || fallback;
}

/**
 * Split a template into literal text and variable tokens for the chip editor.
 * @param {string|null|undefined} template
 * @param {string} [fallback]
 * @returns {Array<{ type: 'text', value: string } | { type: 'var', key: 'parent'|'site'|'title' }>}
 */
export function parseShareTitleTemplate(template, fallback = SHARE_TITLE_DEFAULT) {
    const source = normalizeShareTitleTemplate(template, fallback);
    const parts = [];
    const re = /\{(parent|site|title)\}/gi;
    let last = 0;
    let match;
    while ((match = re.exec(source)) !== null) {
        if (match.index > last) {
            parts.push({ type: 'text', value: source.slice(last, match.index) });
        }
        parts.push({ type: 'var', key: /** @type {'parent'|'site'|'title'} */ (match[1].toLowerCase()) });
        last = re.lastIndex;
    }
    if (last < source.length) {
        parts.push({ type: 'text', value: source.slice(last) });
    }
    return parts.length ? parts : [{ type: 'text', value: '' }];
}

/**
 * @param {Array<{ type: 'text', value: string } | { type: 'var', key: string }>} parts
 * @returns {string}
 */
export function serializeShareTitleParts(parts) {
    return parts
        .map((part) => (part.type === 'var' ? `{${part.key}}` : String(part.value ?? '')))
        .join('');
}

/**
 * Replace `{parent}`, `{site}`, `{title}` and tidy empty separator segments.
 * @param {string|null|undefined} template
 * @param {{ parent?: string, site?: string, title?: string }} vars
 * @param {string} [fallback]
 * @param {{ strict?: boolean }} [opts] strict skips the “use another variable” fallback
 * @returns {string}
 */
export function resolveShareTitle(template, vars = {}, fallback = SHARE_TITLE_DEFAULT, opts = {}) {
    const values = {
        parent: String(vars.parent ?? '').trim(),
        site: String(vars.site ?? '').trim(),
        title: String(vars.title ?? '').trim(),
    };
    let out = normalizeShareTitleTemplate(template, fallback).replace(
        /\{(parent|site|title)\}/gi,
        (_, key) => values[String(key).toLowerCase()] || '',
    );
    // Drop separator runs left by empty variables: " – Tofjan", "Work – ", "a –  – b"
    out = out
        .replace(/^[–—|,\-\s]+/, '')
        .replace(/[–—|,\-\s]+$/, '')
        .replace(/\s*[–—|,]\s*(?=[–—|,])/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
    if (opts.strict) return out;
    return out || values.title || values.parent || values.site || '';
}

/**
 * Variable bag for a page share card / public og:title.
 * @param {object|null|undefined} page
 * @param {{ pages?: object, siteTitle?: string }} [opts]
 */
export function shareTitleVarsForPage(page, opts = {}) {
    const pages = opts.pages || {};
    const parentId = page?.parentId || page?.parentUrl || null;
    const parentPage = parentId ? pages[parentId] : null;
    return {
        parent: String(parentPage?.name || '').trim(),
        site: String(opts.siteTitle ?? '').trim(),
        title: String(page?.name || '').trim(),
    };
}

/**
 * Resolve the public share / document title for a page.
 * @param {object|null|undefined} page
 * @param {{ pages?: object, siteTitle?: string, properties?: object }} [opts]
 */
export function resolvePageShareTitle(page, opts = {}) {
    const siteTitle = opts.siteTitle ?? opts.properties?.site_title ?? '';
    const vars = shareTitleVarsForPage(page, { pages: opts.pages, siteTitle });
    return (
        resolveShareTitle(page?.og?.title, vars, PAGE_SHARE_TITLE_DEFAULT)
        || vars.title
        || ''
    );
}
